import { createSelector } from '@reduxjs/toolkit'

import { pick } from 'lodash'

import { AppStore, RootState } from 'app/state/types'
import { FeatureFlag } from 'app/ui/useFeatureFlag'
import { getFeatureFlag } from 'features/common/state/commonSlice'
import { localFilterComments } from 'features/collaboration/utils/commentUtils'
import { CommentAction, CommentFilter, CommentFilterType } from 'features/collaboration/types'
import { AlertSeverity, Notification, showNotification } from 'features/notifications/state/notificationSlice'
import {
  isMapCommentResponse,
  MapComment,
  MapCommentAnchor,
  MapCommentResponse,
  MapCommentWithResponses
} from 'features/maps/types'
import * as mapCommentsApi from 'features/maps/api/mapComments'
import { deleteMapComment, NewMapComment, NewMapCommentResponse } from 'features/maps/api/mapComments'

import { BuilderMode } from '../types'
import { deleteComment, mapCommentsActions } from '../state/mapCommentsSlice'
import { createApiError } from 'features/common/api/apiError'
import { BuilderPaths } from '../paths'
import copyString from 'features/common/utils/copyString'
import { pathToAbsoluteUrl, uriAddSearchParams } from 'features/common/utils/navigationUtils'
import { MetricEventType } from 'features/metrics/api/metrics'
import { MapCommentPayloadCommon } from 'features/metrics/types'
import { getMentionedUserIds } from 'features/collaboration/utils/mentions'
import { sendMetricEvent } from 'features/metrics/model/metricsMutations'
import { countHyperlinks } from 'features/common/utils/urlUtils'
import { cssSmallMobileBreakpoint } from '../ui/useMobileBuilder'
import { getMapPanelSide } from '../utils/mapUtils'
import { isMapSplit } from '../state/keplerGlSlice'
import { RequestStatus } from 'features/common/types'

export function mapCommentsEnabledSelector(state: RootState) {
  const mapCommentsFF = getFeatureFlag(state, FeatureFlag.BuilderMapComments)
  const builderMode = state.builder.builderMode
  const mapSettings = state.maps.mapSettings

  return (
    mapCommentsFF &&
    (builderMode === BuilderMode.Editor || (builderMode === BuilderMode.Viewer && mapSettings?.comments))
  )
}

export function isCommentFilterRemote(commentsFilter: CommentFilter) {
  return commentsFilter.searchText.length > 0 || commentsFilter.filter === CommentFilterType.MyComments
}

export const prefilteredCommentsSelector = createSelector(
  (state: RootState) => state.mapComments.comments,
  (state: RootState) => state.mapComments.commentsFilter,
  (state: RootState) => state.mapComments.commentsSearchResult,
  (comments, commentsFilter, commentsSearchResult) => {
    const isRemote = isCommentFilterRemote(commentsFilter)

    const remoteComments = commentsSearchResult?.data
    return {
      isRemote,
      comments: isRemote && remoteComments ? remoteComments : comments,
      isLoading: isRemote && commentsSearchResult?.status === RequestStatus.Pending
    }
  }
)

export const visibleCommentsSelector = createSelector(
  prefilteredCommentsSelector,
  (state: RootState) => state.mapComments.commentsVisible,
  (state: RootState) => state.mapComments.commentsFilter,
  ({ comments }, commentsVisible, commentsFilter) => {
    if (!commentsVisible) return []

    return localFilterComments(comments, commentsFilter) as MapCommentWithResponses[]
  }
)

export const hasUnreadCommentsSelector = createSelector(
  (state: RootState) => state.mapComments.comments,
  (comments) => comments.some((c) => !c.read)
)

export const unresolvedCommentsInMapPanelSelector = createSelector(
  (state: RootState) => state.mapComments.comments,
  (_: unknown, mapIndex: number) => mapIndex,
  (comments, mapIndex) => comments.filter((c) => !c.resolved && c.anchor.mapIndex === mapIndex)
)

export async function createMapComment(
  comment: NewMapComment,
  { store }: { store: AppStore }
): Promise<MapComment | undefined> {
  try {
    const savedComment = await mapCommentsApi.createMapComment(comment)

    sendMapCommentEvent({
      action: MetricEventType.BuilderCommentCreated,
      value: savedComment,
      store
    })

    store.dispatch(mapCommentsActions.addComment(savedComment))
    store.dispatch(mapCommentsActions.updateActiveComment(savedComment))

    return savedComment
  } catch (error) {
    console.error(`Failed to save comment: ${error}`, error)
    store.dispatch(showCommentErrorNotification('save comment', error))
  }
}

export async function createMapCommentResponse(
  newResponse: NewMapCommentResponse,
  { store }: { store: AppStore }
): Promise<MapCommentResponse | undefined> {
  try {
    const savedResponse = await mapCommentsApi.createMapCommentResponse(newResponse)

    sendMapCommentEvent({
      action: MetricEventType.BuilderCommentReplyCreated,
      value: savedResponse,
      store
    })

    // update parent comment with response in redux
    // this should happen also thanks to sync, but let's do it immediately
    {
      const parentComment = findComment(newResponse.commentId, store.getState())

      const updatedParentComment = {
        ...parentComment,
        resolved: false,
        responses: [...parentComment.responses, savedResponse],
        responsesCount: parentComment.responsesCount ? parentComment.responsesCount + 1 : 1
      }
      store.dispatch(mapCommentsActions.updateComment(updatedParentComment))

      if (parentComment.resolved) {
        store.dispatch(showCommentNotification('collaboration.comments.notifications.commentReopened'))
      }
    }

    return savedResponse
  } catch (error) {
    console.error(`Failed to create new comment response: ${error}`, error)
    store.dispatch(showCommentErrorNotification('save response', error))
  }
}

export async function toggleThreadResolved(
  commentId: string,
  { store, skipNotification = false }: { store: AppStore; skipNotification?: boolean }
) {
  try {
    const comment = findComment(commentId, store.getState())

    const newResolved = !comment.resolved

    if (newResolved) {
      await mapCommentsApi.resolveMapComment({ mapId: comment.mapId, id: comment.id })
    } else {
      await mapCommentsApi.reopenMapComment({ mapId: comment.mapId, id: comment.id })
    }
    store.dispatch(mapCommentsActions.updateComment({ ...comment, resolved: newResolved }))

    const activeComment = store.getState().mapComments.activeComment
    if (newResolved && activeComment?.id === commentId) {
      store.dispatch(mapCommentsActions.updateActiveComment(undefined))
    }

    if (!skipNotification) {
      sendMapCommentEvent({
        action: newResolved ? MetricEventType.BuilderCommentResolved : MetricEventType.BuilderCommentReopened,
        value: comment,
        store
      })
      store.dispatch(
        showCommentNotification(
          newResolved
            ? 'collaboration.comments.notifications.commentResolved'
            : 'collaboration.comments.notifications.commentReopened'
        )
      )
    }
  } catch (error) {
    console.error(`Failed to toggle comment resolve status: ${error}`, error)
    store.dispatch(showCommentErrorNotification('toggle comment resolve status', error))
  }
}

export async function markCommentsAsResolved(comments, { store }: { store: AppStore }) {
  try {
    const unresolvedComments = comments.filter((c) => !c.resolved)
    await Promise.all(unresolvedComments.map((c) => toggleThreadResolved(c.id, { store, skipNotification: true })))
  } catch (error) {
    console.error(`Failed to mark comment as resolved: ${error}`, error)
  }
}

export async function moveCommentsToMap(
  { oldMapIndex, newMapIndex }: { oldMapIndex: number; newMapIndex: number },
  { store }: { store: AppStore }
) {
  try {
    const comments = store.getState().mapComments.comments
    const commentsToMove = comments.filter((c) => c.anchor.mapIndex === oldMapIndex)
    const movedComments = commentsToMove.map((c) => ({
      id: c.id,
      mapId: c.mapId,
      anchor: { ...c.anchor, mapIndex: newMapIndex }
    }))
    store.dispatch(mapCommentsActions.updateComments(movedComments))
    const mapCommentUpdates = movedComments.map((c) => pick(c, ['mapId', 'id', 'anchor']))

    await Promise.all(mapCommentUpdates.map((c) => mapCommentsApi.updateMapComment(c)))
  } catch (error) {
    console.error(`Failed to move comments to map: ${error}`, error)
    store.dispatch(showCommentErrorNotification('failed to update comments location', error))
  }
}

export async function updateComment(update: mapCommentsApi.MapCommentUpdate, { store }: { store: AppStore }) {
  try {
    const comment = findComment(update.id, store.getState())

    const updated = await mapCommentsApi.updateMapComment(update)
    store.dispatch(mapCommentsActions.updateComment(updated))

    sendMapCommentEvent({
      action: MetricEventType.BuilderCommentEdited,
      value: { ...comment, ...update },
      store
    })
  } catch (error) {
    console.error(`Failed to update comment: ${error}`, error)
    store.dispatch(showCommentErrorNotification('update comment', error))
  }
}

export async function updateCommentResponse(
  update: mapCommentsApi.MapCommentResponseUpdate,
  { store }: { store: AppStore }
) {
  try {
    const response = findCommentResponse(update.id, update.commentId, store.getState())

    // API returns only {updated}, so we just commit what we think we saved
    await mapCommentsApi.updateMapCommentResponse(update)
    store.dispatch(mapCommentsActions.updateCommentResponse(update))

    sendMapCommentEvent({
      action: MetricEventType.BuilderCommentReplyEdited,
      value: { ...response, ...update },
      store
    })
  } catch (error) {
    console.error(`Failed to update comment response: ${error}`, error)
    store.dispatch(showCommentErrorNotification('update comment response', error))
  }
}

export async function markThreadAsRead(commentId: string, { store }: { store: AppStore }) {
  try {
    const mapId = store.getState().maps.currentMap?.id
    if (!mapId) throw new Error('Map not initialized')

    await mapCommentsApi.markCommentsAsRead({ mapId, commentIds: [commentId] })

    const comment = store.getState().mapComments.comments.find((c) => c.id === commentId)
    if (!comment) {
      throw new Error(`Comment not found: ${commentId}`)
    }
    const updatedComment = { ...comment, read: true }
    store.dispatch(mapCommentsActions.updateComment(updatedComment))
  } catch (e) {
    console.error('Failed to mark thread as read', e)
    store.dispatch(showCommentErrorNotification('mark thread as read', e))
  }
}

export async function markAllAsRead({ store }: { store: AppStore }) {
  try {
    const mapId = store.getState().maps.currentMap?.id
    if (!mapId) throw new Error('Map not initialized')

    const unreadCommentIds = store
      .getState()
      .mapComments.comments.filter((c) => !c.read)
      .map((c) => c.id)

    await mapCommentsApi.markCommentsAsRead({ mapId, commentIds: unreadCommentIds })

    const updatedComments = store
      .getState()
      .mapComments.comments.filter((c) => unreadCommentIds.includes(c.id))
      .map((c) => ({ ...c, read: true }))

    store.dispatch(mapCommentsActions.updateComments(updatedComments))

    sendMetricEvent(MetricEventType.BuilderCommentsMarkAllAsRead, {
      mapId,
      commentIds: unreadCommentIds
    })

    store.dispatch(
      showCommentNotification('collaboration.comments.notifications.commentsMarkedAsRead', {
        formatValues: { count: updatedComments.length }
      })
    )
  } catch (e) {
    console.error('Failed to mark threads as read', e)
    store.dispatch(showCommentErrorNotification('mark all as read', e))
  }
}

export async function deleteThread(commentId: string, { store }: { store: AppStore }) {
  try {
    const comment = findComment(commentId, store.getState())

    store.dispatch(deleteComment(commentId))
    await deleteMapComment({ mapId: comment.mapId, id: comment.id })

    sendMapCommentEvent({
      action: MetricEventType.BuilderCommentDeleted,
      value: comment,
      store
    })

    store.dispatch(showCommentNotification('collaboration.comments.notifications.threadDeleted'))
  } catch (e) {
    console.error('Failed to delete thread', e)
    store.dispatch(showCommentErrorNotification('delete thread', e))
  }
}

export async function deleteMapCommentResponse(id: string, commentId: string, { store }: { store: AppStore }) {
  try {
    const comment = findComment(commentId, store.getState())
    const response = findCommentResponse(id, commentId, store.getState())

    const updatedComment = {
      ...comment,
      responses: comment.responses.filter((r) => r.id !== id)
    }
    updatedComment.responsesCount = updatedComment.responses.length
    store.dispatch(mapCommentsActions.updateComment(updatedComment))
    await mapCommentsApi.deleteMapCommentResponse({ mapId: comment.mapId, id, commentId })

    sendMapCommentEvent({
      action: MetricEventType.BuilderCommentReplyDeleted,
      value: response,
      store
    })

    store.dispatch(showCommentNotification('collaboration.comments.notifications.commentDeleted'))
  } catch (e) {
    console.error('Failed to delete map comment response', e)
    store.dispatch(showCommentErrorNotification('delete map comment response', e))
  }
}

export async function handleCommentAction(comment: MapComment, action: CommentAction, { store }: { store: AppStore }) {
  switch (action) {
    case 'toggleResolved':
      await toggleThreadResolved(comment.id, { store })
      break
    case 'delete':
      await deleteThread(comment.id, { store })
      break
    case 'copyLink':
      {
        const link = uriAddSearchParams(BuilderPaths.EditMap(comment.mapId), { commentId: comment.id })
        copyString(pathToAbsoluteUrl(link))
        store.dispatch(showCommentNotification('collaboration.comments.notifications.linkCopied'))
      }
      break
    default:
      break
  }
}

//
// internals
//

function findComment(id: string, state: RootState) {
  const comment = state.mapComments.comments.find((c) => c.id === id)
  if (!comment) {
    throw new Error(`Comment not found: ${id}`)
  }
  return comment
}

function findCommentResponse(id: string, commentId: string, state: RootState) {
  const comment = findComment(commentId, state)
  const response = comment.responses.find((r) => r.id === id)
  if (!response) {
    throw new Error(`Comment response not found: ${commentId} of ${id}`)
  }
  return response
}

export const showCommentNotification = (labelId: string, props?: Partial<Notification>) =>
  showNotification({
    msg: labelId,
    autoWidth: true,
    type: AlertSeverity.Neutral,
    formatted: true,
    closeable: false,
    ...props
  })

const showCommentErrorNotification = (actionLabel: string, error: unknown) =>
  showNotification({
    msg: `Failed to ${actionLabel}`,
    autoWidth: true,
    type: AlertSeverity.Error,
    error: error ? createApiError(error) : undefined,
    formatted: false
  })

export type MapCommentEventType =
  | MetricEventType.BuilderCommentCreated
  | MetricEventType.BuilderCommentReplyCreated
  | MetricEventType.BuilderCommentResolved
  | MetricEventType.BuilderCommentReopened
  | MetricEventType.BuilderCommentEdited
  | MetricEventType.BuilderCommentReplyEdited
  | MetricEventType.BuilderCommentDeleted
  | MetricEventType.BuilderCommentReplyDeleted

export function sendMapCommentEvent({
  action,
  value,
  store
}: {
  action: MapCommentEventType
  value: MapComment | MapCommentResponse
  store: AppStore
}) {
  let parentComment: MapComment | undefined
  let anchor: MapCommentAnchor | undefined
  if (isMapCommentResponse(value)) {
    parentComment = findComment(value.commentId, store.getState())
    anchor = parentComment.anchor
  } else {
    anchor = value.anchor
  }

  const payload: MapCommentPayloadCommon = {
    mapId: value.mapId,
    authorId: value.author.id,
    commentId: isMapCommentResponse(value) ? value.commentId : value.id,
    anchor: getEventPayloadAnchor(anchor, { store })
  }
  if (action === MetricEventType.BuilderCommentDeleted || action === MetricEventType.BuilderCommentReplyDeleted) {
    sendMetricEvent(action, payload)
    return
  }

  const users = store.getState().mapComments.users
  const mentionedUserIds = getMentionedUserIds(value.content, users)
  sendMetricEvent(action, {
    ...payload,
    contentLength: value.content.length,
    hyperlinksCount: countHyperlinks(value.content),
    mentions: mentionedUserIds
  })
}

function getEventPayloadAnchor(anchor: MapCommentAnchor | undefined, { store }: { store: AppStore }) {
  if (!anchor) return undefined

  const side = isMapSplit(store.getState())
    ? getMapPanelSide({ mapIndex: anchor.mapIndex, isMobile: matchMedia(cssSmallMobileBreakpoint).matches })
    : undefined
  return { latitude: anchor.latitude, longitude: anchor.longitude, zoom: anchor.zoom, side }
}
